import React, { useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from '@xyflow/react';
import { Repeat, X } from 'lucide-react';
import { LoopNodeData, LOOP_HANDLE_BODY, LOOP_HANDLE_AFTER } from '../types';

/**
 * Loop node — iterates over an array (itemsPath) and runs its body once per
 * item. Two source handles:
 *   - 'body'  → the action to run for each item (single action node)
 *   - 'after' → continues the main flow once all iterations finish
 */
export function LoopNode({ id, data: rawData, selected }: NodeProps) {
  const data = rawData as unknown as LoopNodeData;
  const [isHovered, setIsHovered] = useState(false);
  const { setNodes, setEdges } = useReactFlow();

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setNodes((nodes) => nodes.filter((n) => n.id !== id));
    setEdges((edges) => edges.filter((e) => e.source !== id && e.target !== id));
  };

  return (
    <div
      className={`relative min-w-[220px] rounded-xl border-2 bg-white shadow-lg transition-all ${
        selected ? 'border-cyan-500 shadow-cyan-200' : 'border-cyan-400'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isHovered && (
        <button
          onClick={handleDelete}
          className="absolute -top-2 -right-2 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow-md hover:bg-red-600 transition-colors"
          title="Delete node"
        >
          <X size={12} />
        </button>
      )}
      <Handle type="target" position={Position.Top} className="!bg-cyan-500 !w-3 !h-3" />
      <div className="flex items-center gap-2 rounded-t-xl bg-cyan-500 px-3 py-2">
        <Repeat size={14} className="text-white" />
        <span className="text-xs font-semibold uppercase tracking-wider text-white">Loop</span>
      </div>
      <div className="px-3 py-2">
        <p className="text-xs text-gray-500 dark:text-gray-400">For each item in</p>
        <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
          {data.displayLabel || data.itemsPath || 'No array selected'}
        </p>
        {data.mode && data.mode !== 'serial' && (
          <p className="mt-0.5 text-[10px] text-cyan-600">
            {data.mode === 'parallel_limited' ? `parallel ×${data.concurrency ?? 1}` : 'parallel'}
          </p>
        )}
      </div>
      {/* Two output handles: body (per-item) + after (continue) */}
      <div className="flex justify-around gap-3 px-3 py-2 border-t border-cyan-200 bg-cyan-50 rounded-b-xl">
        <div className="flex flex-col items-center">
          <span className="text-[10px] font-semibold text-cyan-700 mb-1">Each item</span>
          <Handle
            type="source"
            position={Position.Bottom}
            id={LOOP_HANDLE_BODY}
            className="!bg-cyan-500 !w-3 !h-3 !relative !transform-none !left-0 !right-auto !bottom-0"
            style={{ position: 'relative' }}
          />
        </div>
        <div className="flex flex-col items-center">
          <span className="text-[10px] font-semibold text-gray-600 mb-1">After</span>
          <Handle
            type="source"
            position={Position.Bottom}
            id={LOOP_HANDLE_AFTER}
            className="!bg-gray-500 !w-3 !h-3 !relative !transform-none !left-0 !right-auto !bottom-0"
            style={{ position: 'relative' }}
          />
        </div>
      </div>
    </div>
  );
}